import { inject } from "@angular/core";
import {
  signalStore,
  withState,
  withMethods,
  patchState
} from "@ngrx/signals";
import {
  FilterSet,
  FilterSetType,
} from "src/app/core/interfaces/filter-interface";
import { Pagination } from "src/app/core/interfaces/pagination";
import { VideoTileDto } from "src/app/core/interfaces/tile-interface";

import { FiltersService } from "src/app/core/services/filters/filters-service";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";

interface VideoSearchState {
  branchesFilters: FilterSet;
  branchesFiltersSelected: FilterSet;
  activitiesFilters: FilterSet;
  activitiesFiltersSelected: FilterSet;
  languageFilters: FilterSet;
  results: VideoTileDto[];
  pagination: Pagination;
  totalVideosFound: number;
}

const initialState: VideoSearchState = {
  branchesFilters: null,
  branchesFiltersSelected: null,
  activitiesFilters: null,
  activitiesFiltersSelected: null,
  languageFilters: null,
  results: [],
  pagination: null,
  totalVideosFound: 0,
};

export const VideoSearchStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withMethods((store) => {
    const tileService = inject(TilesApiService);
    const filtersService = inject(FiltersService);

    return {
      setFilters(branchesFilters, activitiesFilters, languageFilters): void {
        patchState(store, {
          branchesFilters,
          activitiesFilters,
          languageFilters,
        });
      },
      branchesFilterExists(): boolean {
        return store.branchesFilters() !== null;
      },
      activitiesFilterExists(): boolean {
        return store.activitiesFilters() !== null;
      },
      anyResults(): boolean {
        return store.results().length > 0;
      },
      setResults(results: VideoTileDto[]) {
        patchState(store, { results });
      },
      setPagination(pagination: Pagination) {
        patchState(store, { pagination });
      },
      setTotalVideosFound(total: number) {
        patchState(store, { totalVideosFound: total });
      },
      setFiltersSelected(
        branchesFiltersSelected: FilterSet,
        activitiesFiltersSelected: FilterSet
      ): void {
        patchState(store, {
          branchesFiltersSelected,
          activitiesFiltersSelected,
        });
      },
      async getVideoTiles(): Promise<VideoTileDto[]> {
        const selectedFilters = [
          store.activitiesFiltersSelected(),
          store.branchesFiltersSelected(),
        ];

        const currentPagination = store.pagination();
        const videoResult = await tileService.getVideoResult(
          selectedFilters,
          currentPagination
        );
        this.setTotalVideosFound(videoResult.pagination.total);
        const videoTiles = videoResult.videoTiles;

        this.setPagination(videoResult.pagination);
        return videoTiles;
      },
      async loadVideos(): Promise<void> {
        const videoTiles = await this.getVideoTiles();
        this.setResults(videoTiles);
      },
      async loadMoreVideos(): Promise<void> {
        const videoTiles: VideoTileDto[] = await this.getVideoTiles();
        this.setResults([...store.results(), ...videoTiles]);
      },
      async loadVideoFilters(): Promise<void> {
        const filterGroup = await filtersService.getFilterGroup();

        const filterSetSector = filterGroup.filterSets.find(
          (fs) => fs.filterSetType == FilterSetType.Sector
        );
        const filterSetActivity = filterGroup.filterSets.find(
          (fs) => fs.filterSetType == FilterSetType.Activity
        );
        const filterSetLanguage = filterGroup.filterSets.find(
          (fs) => fs.filterSetType == FilterSetType.Language
        );

        this.setFilters(filterSetSector, filterSetActivity, filterSetLanguage);
      },
      updateSelectedFiltersTranslations(): void {
        const branchesFilters = store.branchesFilters();
        const selectedBranchesFilters = store.branchesFiltersSelected();
        const activitiesFilters = store.activitiesFilters();
        const selectedActivitiesFilters = store.activitiesFiltersSelected();

        this.setFiltersSelected(
          selectedBranchesFilters && branchesFilters
            ? filtersService.updateFilterSetTranslations(
                selectedBranchesFilters,
                branchesFilters
              )
            : selectedBranchesFilters,
          selectedActivitiesFilters && activitiesFilters
            ? filtersService.updateFilterSetTranslations(
                selectedActivitiesFilters,
                activitiesFilters
              )
            : selectedActivitiesFilters
        );
      },
    };
  })
);
